import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FaCommentAlt, FaPrint } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi2';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { Link, useParams } from 'react-router-dom';
import { ActionIcon, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import StatusDropdown from '../../../../../components/forms/StatusDropdown';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../../../components/providers/FiltersProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import useContractInvoiceStates from '../../../../../hooks/api-calls/contract/useContractInvoicingStates';
import useContractProductsStates from '../../../../../hooks/api-calls/contract/useContractProductsStates';
import useContractStates from '../../../../../hooks/api-calls/contract/useContractStates';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useFetch from '../../../../../hooks/useFetch';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import useDepartmentBlockedContractStatuses from '../../../../../hooks/useDepartmentBlockedContractStatuses';
import Page from '../../../../../layout/Page/Page';
import { menuRoutes } from '../../../../../router/menu';
import { ContractService } from '../../../../../services/contract/contractService';
import { ContractsApiResponse } from '../../../../../type/entities/contract-type';
import { fixNumber } from '../../../../../utils/formatNumber';
import { isProductStatusLocked } from '../../../../../utils/contractProductStatusLock';
import UserContractsFilters from './UserContractsFilters';

interface ContractsListProps { }

const columnOrder = [
    'id',
    'code',
    'currentContractStatus',
    'currentContractStatusDate',
    'currentProductStatus',
    'currentContractProductStatusDate',
    'currentInvoicingStatus',
    'currentContractInvoicingStatusDate',
    'currentIncidenceStatus',
    'currentContractIncidenceStatusDate',
    'productType',
    'product',
    'clientProvince',
    'client',
    'clientCifNif',
    'totalAmount',
    'createdAt',
    'updatedAt',
    'invoice',
    'incidence',
    'complements'
];

const UserContractsList: React.FC<ContractsListProps> = ({ }) => {

    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const { handleErrors } = useHandleErrors();
    const canChangeStatus = userCan('edit_contracts', 'contracts') && userCan('get_statuses_for_select', 'statuses');
    const departmentBlockedContractStatusIds = useDepartmentBlockedContractStatuses();
    const { getContractStatesList, isLoadingContractStates } = useContractStates({ action: 'edit', entity: 'contract' });
    const { getContractProductsStatesList, isLoadingContractProductsStates } = useContractProductsStates({ action: 'edit', entity: 'contract_product' });
    const { getContractInvoiceStatesList, isLoadingContractInvoiceStates } = useContractInvoiceStates({ action: 'edit', entity: 'contract_invoicing' });
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new ContractService();

    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || filters?.filter_filters?.commercial !== id) return null;
        const response = await service.listContracts(filters);
        return response.getResponseData() as ContractsApiResponse;
    }, [filters]));

    useEffect(() => {
        updateFilters({ commercial: id });
    }, [id])

    const handleDelete = async (id: string) => {
        const response = await (await service.deleteContract(id)).getResponseData();
        if (response.success) {
            toast.success('Contrato eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiContracts(ids)).getResponseData();
        if (response.success) {
            toast.success('Contrato eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const handleStateChange = async (id: string, stateId: string, comment: string, entityId: string, entityName: string) => {
        var response = { success: false };
        switch (entityName) {
            case 'contract':
                response = await (await service.changeContractStatus(id, stateId, comment)).getResponseData();
                if (response.success) toast.success('Estado del contrato actualizado correctamente');
                break;
            case 'invoice':
                response = await (await service.changeContractInvoicingStatus(id, stateId, entityId, comment)).getResponseData();
                if (response.success) toast.success('Estado de la factura actualizado correctamente');
                break;
            case 'product':
                response = await (await service.changeContractProductStatus(id, stateId, entityId, comment)).getResponseData();
                if (response.success) toast.success('Estado del producto actualizado correctamente');
                break;
            case 'incidence':
                // response = await (await service.changeIncidenceState(id, stateId, entityId, comment)).getResponseData();
                if (response.success) toast.success('Estado de la incidencia actualizado correctamente');
                break;

            default:
                break;
        }
        if (!response.success) {
            toast.error('Error al actualizar el estado');
        }
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    return (
        <Page container='fluid' className='mt-4'>
            {!isLoadingContractStates && !isLoadingContractProductsStates && !isLoadingContractInvoiceStates && data !== undefined
                ? (
                    <CustomTable
                        largeTable
                        id={'contracts-table'}
                        columnOrder={columnOrder}
                        data={orderedData()}
                        isLoading={loading}
                        columnsNotShown={['currentContractProductStatusDate', 'currentContractInvoicingStatusDate', 'currentContractIncidenceStatusDate', 'currentContractStatusDate', 'currentIncidenceStatus', 'invoice', 'incidence', 'complements', 'isDraft']}
                        overrideColumns={[
                            {
                                key: 'code',
                                render: (row: any) => {
                                    return (
                                        userCan('get_contracts', 'contracts')
                                            ? (
                                                <Link to={`${menuRoutes.contracts.path}/${row.row.original.id}/profile${menuRoutes.contracts.profile.info}`}>
                                                    <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                                </Link>
                                            )
                                            : <Text className='font-bold text-primary'>{row.row.original.code}</Text>
                                    );
                                }
                            },
                            {
                                key: 'client',
                                render: (row: any) => {
                                    const client = row.row.original?.client;
                                    return (
                                        <>
                                            {client
                                                ? userCan('get_clients', 'clients')
                                                    ? (
                                                        <div className='flex justify-center items-center'>
                                                            <Link
                                                                to={`${menuRoutes.clients.path}/${client?.id}/profile/info`}
                                                                className='font-bold text-primary cursor-pointer'
                                                            >
                                                                {row.row.original?.clientCompanyName}
                                                            </Link>
                                                            {userCan('get_clients_comments', 'clients') && client.lastComment && (
                                                                <Tooltip
                                                                    size="sm"
                                                                    content={<div className='tooltip-container'>{client.lastComment}</div>}
                                                                    placement="top"
                                                                    color="invert"
                                                                >
                                                                    <ActionIcon
                                                                        as="span"
                                                                        size="sm"
                                                                        variant="text"
                                                                        className="hover:!border-gray-900 hover:text-gray-700"
                                                                    >
                                                                        <FaCommentAlt className="h-4 w-4" size={20} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    )
                                                    : 'N/A'
                                                :
                                                (<span className='font-bold text-primary'>{row.row.original?.clientCompanyName}</span>)
                                            }
                                        </>
                                    )
                                }
                            },
                            {
                                key: 'product',
                                render: (row: any) => {
                                    const product = row.row.original?.product;
                                    const complements = row.row.original?.complements;
                                    return (
                                        <div className='flex flex-col'>
                                            {product ?
                                                (<Link
                                                    to={`${menuRoutes.products.path}?filter_filters=%7B"active"%3Atrue%2C"search_text"%3A"${product.name}"%7D&filter_order=%5B%5D&page=1&limit=50`}
                                                    className='font-bold text-primary'
                                                >
                                                    {product.name}
                                                </Link>
                                                )
                                                : 'N/A'}

                                            {product && complements && complements.length >= 1 && (
                                                <div className='flex flex-col gap-1 mt-2 font-semibold'>
                                                    <Text className='font-semibold text-md'>Complementos:</Text>
                                                    <ul className='indent-4'>
                                                        {complements.map((complement: any, index: number) => (
                                                            <li
                                                                key={product.id + index}
                                                                className='text-md'
                                                            >
                                                                <Text className='text-xs text-nowrap'>- {complement.complements.name} ({fixNumber(complement.amount)}€)</Text>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>)
                                            }


                                        </div>
                                    )
                                }
                            },
                            {
                                key: 'productType',
                                render: (row: any) => {
                                    return row.row.original?.productType?.name;
                                }
                            },
                            {
                                key: 'currentContractStatus',
                                render: (row: any) => {
                                    return (
                                        <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                            <StatusDropdown
                                                entityId={row.row.original?.id}
                                                title="¿Estás seguro de cambiar el estado del contrato?"
                                                disabled={row.row.original?.isDraft || !canChangeStatus}
                                                // Get the list of states for the current product type
                                                statesOptions={getContractStatesList(null, row.row.original?.currentContractStatus?.id).filter((state: any) => state.productTypes?.includes(row.row.original?.productType?.id))}
                                                currentState={row.row.original?.currentContractStatus}
                                                currentStateDate={row.row.original?.currentContractStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string) =>
                                                    handleStateChange(entityId, statusId, comment, entityId, 'contract')
                                                }
                                            />
                                        </div>
                                    );
                                }
                            },
                            {
                                key: 'currentInvoicingStatus',
                                render: (row: any) => {
                                    const invoiceStatusDate = row.row.original?.currentContractInvoicingStatusDate?.date;
                                    const invoiceStatus = row.row.original?.currentInvoicingStatus;
                                    const invoice = row.row.original?.invoice;
                                    return (
                                        <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                            {invoiceStatus ? (
                                                <StatusDropdown
                                                    entityId={row.row.original?.id}
                                                    title="¿Estás seguro de cambiar el estado de la factura?"
                                                    disabled={!invoice || row.row.original?.isDraft || !canChangeStatus}
                                                    statesOptions={getContractInvoiceStatesList(null, invoiceStatus.id)}
                                                    currentState={invoiceStatus}
                                                    currentStateDate={invoiceStatusDate}
                                                    handleStateChange={(entityId: string, statusId: string, comment: string) =>
                                                        handleStateChange(entityId, statusId, comment, invoice?.id ?? '', 'invoice')
                                                    }
                                                />
                                            ) : "N/A"}
                                        </div>
                                    );
                                }
                            },
                            {
                                key: 'currentProductStatus',
                                render: (row: any) => {
                                    const productStatusDate = row.row.original?.currentContractProductStatusDate?.date;
                                    const productStatus = row.row.original?.currentProductStatus;
                                    const product = row.row.original?.product;
                                    const isProductLocked = isProductStatusLocked(
                                        row.row.original?.currentContractStatus?.id,
                                        departmentBlockedContractStatusIds
                                    );
                                    return (
                                        <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id}>
                                            {(productStatusDate && productStatus) ? (
                                                <StatusDropdown
                                                    entityId={row.row.original?.id}
                                                    title="¿Estás seguro de cambiar el estado del producto?"
                                                    disabled={row.row.original?.isDraft || !canChangeStatus || isProductLocked}
                                                    statesOptions={getContractProductsStatesList(null, productStatus.id)}
                                                    currentState={productStatus}
                                                    currentStateDate={productStatusDate}
                                                    handleStateChange={(entityId: string, statusId: string, comment: string) =>
                                                        handleStateChange(entityId, statusId, comment, product.id, 'product')
                                                    }
                                                />
                                            ) : "N/A"}
                                        </div>
                                    );
                                }
                            },
                            // {
                            //     key: 'currentIncidenceStatus',
                            //     render: (row: any) => {
                            //         const incidenceStatusDate = row.row.original?.currentContractIncidenceStatusDate?.date;
                            //         const incidenceStatus = row.row.original?.currentIncidenceStatus;
                            //         const incidence = row.row.original?.incidence;
                            //         return (
                            //             <div className='flex flex-col gap-2 justify-content-center text-center'>
                            //                 {( incidence && incidenceStatusDate && incidenceStatus) ? (

                            //                         <StatusDropdown
                            //                             entityId={row.row.original?.id}
                            //                             title="¿Estás seguro de cambiar el estado de la incidencia?"
                            //                             statesOptions={[]} // TODO: Add incidence states api-call hook
                            //                             currentState={incidenceStatus}
                            //                             currentStateDate={incidenceStatusDate}
                            //                             handleStateChange={(entityId: string, statusId: string, comment: string) =>
                            //                                 handleStateChange(entityId, statusId, comment, incidence.id, 'product')
                            //                             }
                            //                         />
                            //                 ) : "N/A"}
                            //             </div>
                            //         );
                            //     }
                            // },
                            {
                                key: 'totalAmount',
                                render: (row: any) => {
                                    return (typeof row.row.original?.totalAmount === 'number' && !isNaN(row.row.original?.totalAmount)) ? `${fixNumber(row.row.original?.totalAmount)}€` : 'N/A';
                                }
                            },
                        ]}
                        actions={[
                            {
                                label: 'Generar PDF',
                                permissions: { group: 'contracts', permission: 'print_contract' },
                                render: (row: any) => {
                                    if (row.isDraft) return null;
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Generar PDF'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 cursor-pointer"
                                                onClick={() => handleDownloadDocumentNewTab(service.generateContractPdf, row.id, moment().format('DD-MM-YYYY') + '_contrato.pdf')}
                                            >
                                                <FaPrint className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                label: 'View',
                                permissions: { group: 'user', permission: 'get_user' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Ver detalles'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Link
                                                to={`${menuRoutes.contracts.path}/${row.id}/profile${menuRoutes.contracts.profile.info}`}>
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700"
                                                >
                                                    <HiOutlineEye className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Link>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                label: 'Edit',
                                permissions: { group: 'contracts', permission: 'edit_contracts' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Editar contrato'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Link to={`${menuRoutes.contracts.path}/${row.id}/edit`}>
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700"
                                                >
                                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Link>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                label: 'Delete',
                                permissions: { group: 'contracts', permission: 'delete_contracts' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Eliminar'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <div>
                                                <DeletePopover
                                                    title={`Eliminar contrato`}
                                                    description={`¿Estás seguro de que deseas eliminar el contrato ${row.code}?`}
                                                    onDelete={() => handleDelete(row.id)}
                                                />
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                        ]}
                        handleMultipleDelete={handleMultiDelete}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: (data as ContractsApiResponse) ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    />
                )
                : (<Loader height="70vh" />)
            }

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Contratos'}>
                <UserContractsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default UserContractsList;